const defaultSuggestionClass = 'search-suggestion navigation:p-[6px_10px] mapui:text-[12px] mapui:[color:var(--text-primary)] mapui:[cursor:pointer] navigation:leading-[1.3] mapui:[border-bottom:1px_solid_var(--border-color)] navigation:[&:last-child]:[border-bottom:none] navigation:[&.active]:[background:var(--bg-active)] navigation:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-active)]';
import type { SchemaInput } from '../shared/schema-input.js';
import { isObject, isString } from '../shared/runtime-values.js';
import { AUTOCOMPLETE_REQUEST_SUPERSEDED, createAbortError, isAbortError } from './abort-reasons.js';
import { MAX_GEOCODE_QUERY_LENGTH, isGeocodeQueryTooLong } from '../shared/geocoder-limits.js';
import { reportAutocompleteRequest } from './autocomplete-telemetry.js';
import { t } from './i18n.js';

export interface AutocompleteControllerOptions<T> {
  input: HTMLInputElement;
  suggestionsElement: HTMLElement;
  loadSuggestions: (query: string, signal: AbortSignal) => Promise<T[]>;
  getLabel: (suggestion: T) => string;
  onSelect: (suggestion: T, query: string) => void | Promise<void>;
  onSubmit?: (query: string) => void | Promise<void>;
  onError?: (cause: unknown, query: string) => void;
  onInput?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  selectFirstOnSubmit?: boolean;
  requireExplicitSelection?: boolean;
  emptyMessage?: string;
  minQueryLength?: number;
  debounceMs?: number;
  itemClassName?: string;
  openClassName?: string;
  statusClassName?: string;
}

interface AutocompletePresentation {
  primary: string;
  secondary?: string;
}

function abortAutocompleteRequest(controller: AbortController | null): void {
  controller?.abort(createAbortError(AUTOCOMPLETE_REQUEST_SUPERSEDED));
}

function applyGeocodeQueryLimit(input: HTMLInputElement): void {
  if (input.maxLength < 0 || input.maxLength > MAX_GEOCODE_QUERY_LENGTH) {
    input.maxLength = MAX_GEOCODE_QUERY_LENGTH;
  }
}

function limitGeocodeSuggestions<T>(loadSuggestions: AutocompleteControllerOptions<T>['loadSuggestions']) {
  return (query: string, signal: AbortSignal) => isGeocodeQueryTooLong(query)
    // SAFETY: The generic owner contract stores and returns the same caller-provided type.
    ? Promise.resolve(/* SAFETY: The local owner stores and returns this value with the stated type. */ [] as T[])
    : loadSuggestions(query, signal);
}

function autocompletePresentation(value: SchemaInput): AutocompletePresentation | null {
  if (!value || !isObject(value)) return null;
  // SAFETY: The adjacent boundary checks validate the fields that this code reads from the value.
  const presentation = (value as { autocompletePresentation?: unknown }).autocompletePresentation;
  if (!presentation || !isObject(presentation)) return null;
  // SAFETY: The local owner contract defines this value with the stated concrete type.
  const primary = (presentation as { primary?: unknown }).primary;
  // SAFETY: The local owner contract defines this value with the stated concrete type.
  const secondary = (presentation as { secondary?: unknown }).secondary;
  if (!isString(primary) || !primary) return null;
  return {
    primary,
    secondary: isString(secondary) && secondary ? secondary : undefined,
  };
}

function createSuggestionItem<T>({
  suggestion,
  itemClassName,
  label,
  choose,
}: {
  suggestion: T;
  itemClassName: string;
  label: string;
  choose: (suggestion: T) => void;
}): HTMLElement {
  const item = document.createElement('div');
  item.className = itemClassName === 'search-suggestion' ? defaultSuggestionClass : itemClassName;
  item.setAttribute('role', 'option');
  item.setAttribute('aria-label', label);
  const presentation = autocompletePresentation(suggestion);
  if (presentation) {
    const primary = document.createElement('div');
    primary.className = 'autocomplete-suggestion-primary mapui:[overflow:hidden] mapui:[color:var(--text-primary)] navigation:font-[600] mapui:[text-overflow:ellipsis] mapui:whitespace-nowrap';
    primary.textContent = presentation.primary;
    item.appendChild(primary);
    if (presentation.secondary) {
      const secondary = document.createElement('div');
      secondary.className = 'autocomplete-suggestion-secondary mapui:[overflow:hidden] navigation:mt-[2px] mapui:[color:var(--text-secondary)] mapui:text-[11px] mapui:[text-overflow:ellipsis] mapui:whitespace-nowrap';
      secondary.textContent = presentation.secondary;
      item.appendChild(secondary);
    }
  } else {
    item.textContent = label;
  }
  let selected = false;
  const selectSuggestion = (event: Event) => {
    event.stopPropagation();
    if (selected) return;
    selected = true;
    event.preventDefault();
    choose(suggestion);
  };
  item.addEventListener('pointerdown', (event) => {
    if (!event.isPrimary) return;
    // Keep input focus without selecting before the browser can detect a scroll.
    event.preventDefault();
  });
  item.addEventListener('mousedown', (event) => event.preventDefault());
  item.addEventListener('click', selectSuggestion);
  return item;
}

function createEmptySuggestionsStatus(className: string, message: string): HTMLElement {
  const status = document.createElement('div');
  status.className = className;
  status.setAttribute('role', 'status');
  status.textContent = message;
  return status;
}

function showSearchRetry(element: HTMLElement, input: HTMLInputElement, openClassName: string, retrySearch: () => void): void {
  element.replaceChildren();
  element.setAttribute('role', 'group');
  const errorClass = 'autocomplete-error mapui:p-[10px_12px] mapui:[color:var(--text-secondary)] mapui:text-[13px]';
  const status = createEmptySuggestionsStatus(errorClass, t('toast.search_failed'));
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'autocomplete-retry mapui:block mapui:w-[100%] mapui:min-h-[44px] mapui:p-[10px_12px] mapui:[border:0] mapui:[background:var(--bg-input)] mapui:[color:var(--text-primary)] mapui:text-start mapui:[cursor:pointer] navigation:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-active)] navigation:[&:focus-visible]:[background:var(--bg-active)]';
  retry.textContent = t('route_planner.tryAgain');
  retry.addEventListener('pointerdown', event => event.preventDefault());
  retry.addEventListener('click', event => {
    event.stopPropagation();
    input.focus();
    retrySearch();
  });
  element.appendChild(status);
  element.appendChild(retry);
  element.classList.add(openClassName);
}

export function createHeadlessAutocompleteController<T>({
  loadSuggestions,
  onResults,
  onError,
  onLoadingChange,
  debounceMs,
}: {
  loadSuggestions: (query: string, signal: AbortSignal) => Promise<T[]>;
  onResults: (suggestions: T[]) => void;
  onError?: (cause: unknown, query: string) => void;
  onLoadingChange?: (loading: boolean) => void;
  debounceMs: number;
}) {
  type RequestState =
    | { status: 'idle' }
    | { status: 'scheduled'; timer: ReturnType<typeof setTimeout> }
    | { status: 'loading'; controller: AbortController };
  let state: RequestState = { status: 'idle' };

  function cancel(): void {
    const previous = state;
    state = { status: 'idle' };
    if (previous.status === 'scheduled') clearTimeout(previous.timer);
    if (previous.status === 'loading') abortAutocompleteRequest(previous.controller);
  }

  function close(): void {
    cancel();
    onLoadingChange?.(false);
  }

  async function load(query: string): Promise<T[] | null> {
    cancel();
    const requestController = new AbortController();
    const loading: RequestState = { status: 'loading', controller: requestController };
    state = loading;
    const started = performance.now();
    let outcome: 'success' | 'cancelled' | 'failed' = 'failed';
    onLoadingChange?.(true);
    try {
      const next = await loadSuggestions(query, requestController.signal);
      if (requestController.signal.aborted) { outcome = 'cancelled'; return null; }
      onResults(next);
      outcome = 'success';
      return next;
    } catch (error) {
      if (requestController.signal.aborted || isAbortError(error)) { outcome = 'cancelled'; return null; }
      onError?.(error, query);
      return null;
    } finally {
      reportAutocompleteRequest(outcome, performance.now() - started);
      if (state === loading) {
        state = { status: 'idle' };
        onLoadingChange?.(false);
      }
    }
  }

  function schedule(query: string): void {
    const wasLoading = state.status === 'loading';
    cancel();
    if (wasLoading) onLoadingChange?.(false);
    const timer = setTimeout(() => {
      if (state.status === 'scheduled' && state.timer === timer) void load(query);
    }, debounceMs);
    state = { status: 'scheduled', timer };
  }

  return { close, load, schedule };
}

function configureCombobox(input: HTMLInputElement, list: HTMLElement): void {
  if (list.getAttribute('role') !== 'listbox' || !list.id) return;
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', list.id);
  input.setAttribute('aria-expanded', 'false');
}

function updateActiveSuggestion(items: NodeListOf<Element>, activeIndex: number, input: HTMLInputElement): void {
  items.forEach((item, itemIndex) => {
    item.classList.toggle('active', itemIndex === activeIndex);
    item.setAttribute('aria-selected', String(itemIndex === activeIndex));
  });
  const active = items[activeIndex];
  if (active?.id) input.setAttribute('aria-activedescendant', active.id);
  else input.removeAttribute('aria-activedescendant');
  active?.scrollIntoView?.({ block: 'nearest' });
}

function appendSuggestions<T>(next: T[], suggestionsElement: HTMLElement, input: HTMLInputElement, itemClassName: string, getLabel: (value: T) => string, choose: (value: T) => Promise<void>): void {
  next.forEach((suggestion, index) => {
    const item = createSuggestionItem({
      suggestion,
      itemClassName,
      label: getLabel(suggestion),
      choose: (value) => { void choose(value); },
    });
    item.id = `${suggestionsElement.id || input.id}-option-${index}`;
    item.setAttribute('aria-selected', 'false');
    suggestionsElement.appendChild(item);
  });
}

export function createAutocompleteController<T>(options: AutocompleteControllerOptions<T>) {
  const {
    input,
    suggestionsElement,
    loadSuggestions,
    getLabel,
    onSelect,
    onSubmit,
    onError,
    onInput,
    onLoadingChange,
    selectFirstOnSubmit = false,
    requireExplicitSelection = false,
    emptyMessage,
    minQueryLength = 3,
    debounceMs = 250,
    itemClassName = 'search-suggestion',
    openClassName = 'open',
    statusClassName = 'search-suggestion-status',
  } = options;
  let suggestions: T[] = [];
  let activeIndex = -1;
  const suggestionsRole = suggestionsElement.getAttribute('role');
  applyGeocodeQueryLimit(input);
  configureCombobox(input, suggestionsElement);

  function close({ cancel = true }: { cancel?: boolean } = {}): void {
    if (cancel) headless.close();
    suggestions = [];
    activeIndex = -1;
    suggestionsElement.classList.remove(openClassName);
    if (suggestionsRole === 'listbox') input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  function setActive(index: number): void {
    const items = suggestionsElement.querySelectorAll(`.${itemClassName}`);
    activeIndex = Math.max(-1, Math.min(index, items.length - 1));
    updateActiveSuggestion(items, activeIndex, input);
  }

  async function choose(suggestion: T): Promise<void> {
    const query = input.value.trim();
    input.value = getLabel(suggestion);
    close();
    await onSelect(suggestion, query);
  }

  function render(next: T[]): void {
    if (suggestionsRole) suggestionsElement.setAttribute('role', suggestionsRole);
    else suggestionsElement.removeAttribute('role');
    suggestions = next;
    activeIndex = -1;
    suggestionsElement.innerHTML = '';
    if (next.length === 0 && !emptyMessage) {
      close({ cancel: false });
      return;
    }
    if (next.length === 0 && emptyMessage) {
      suggestionsElement.appendChild(createEmptySuggestionsStatus(statusClassName, emptyMessage));
    }
    appendSuggestions(next, suggestionsElement, input, itemClassName, getLabel, choose);
    suggestionsElement.classList.add(openClassName);
    if (suggestionsRole === 'listbox') input.setAttribute('aria-expanded', 'true');
    input.removeAttribute('aria-activedescendant');
  }

  const headless = createHeadlessAutocompleteController({
    loadSuggestions: limitGeocodeSuggestions(loadSuggestions),
    onResults: render,
    onError: (error, query) => {
      suggestions = [];
      activeIndex = -1;
      showSearchRetry(suggestionsElement, input, openClassName, () => {
        close();
        void headless.load(input.value.trim());
      });
      onError?.(error, query);
    },
    onLoadingChange,
    debounceMs,
  });

  function schedule(): void {
    close();
    onInput?.();
    const query = input.value.trim();
    if (query.length < minQueryLength) {
      close();
      return;
    }
    headless.schedule(query);
  }

  input.addEventListener('input', schedule);
  input.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(activeIndex - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const suggestion = activeIndex >= 0 ? suggestions[activeIndex] : requireExplicitSelection ? undefined : suggestions[0];
      if (suggestion) void choose(suggestion);
      else if (selectFirstOnSubmit) {
        const query = input.value.trim();
        if (!query) return;
        headless.close();
        void headless.load(query).then((loaded) => {
          if (loaded?.[0]) void choose(loaded[0]);
          else if (loaded && onSubmit) void onSubmit(query);
        });
      } else if (onSubmit) {
        close();
        const query = input.value.trim();
        if (query) void onSubmit(query);
      }
    } else if (event.key === 'Escape') close();
  });
  input.addEventListener('blur', () => window.setTimeout(() => {
    if (document.activeElement !== input && !suggestionsElement.contains(document.activeElement)) close();
  }, 150));

  return { close, showSuggestions: render, load: headless.load, setValue: (value: string) => { input.value = value; } };
}
